/**
 * SessionReviewStatsCard — display aggregated session review averages.
 * Shown in admin report detail and public share-link view.
 * Stats are derived on read (not persisted). Mobile-first ≥320px.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import type { SessionReviewStats } from '@client/lib/queries/use-progress-reports';

interface Props {
  stats: SessionReviewStats;
}

interface MetricRowProps {
  label: string;
  value: number | null;
}

function MetricRow({ label, value }: MetricRowProps) {
  const pct = value != null ? (value / 5) * 100 : 0;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold tabular-nums">
          {value != null ? `${value.toFixed(1)}/5` : '—'}
        </span>
      </div>
      <Progress value={value != null ? pct : 0} className="h-1.5" />
    </div>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso;
  }
}

export function SessionReviewStatsCard({ stats }: Props) {
  const { sessionsReviewed, averages, rangeStart, rangeEnd } = stats;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Đánh giá theo buổi</CardTitle>
      </CardHeader>
      <CardContent>
        {sessionsReviewed === 0 || averages === null ? (
          <p className="text-sm text-muted-foreground">
            Chưa có dữ liệu chấm review trong giai đoạn này.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-3 sm:grid sm:grid-cols-3 sm:gap-4">
              <MetricRow label="Tham gia" value={averages.participation} />
              <MetricRow label="Hiểu bài" value={averages.comprehension} />
              <MetricRow label="Hành vi" value={averages.behavior} />
            </div>
            <p className="text-xs text-muted-foreground">
              Dựa trên {sessionsReviewed} buổi đánh giá
              {rangeStart && rangeEnd
                ? ` · ${formatDate(rangeStart)} – ${formatDate(rangeEnd)}`
                : ''}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
